import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useGanttLayout } from './gantt-layout'

interface Props {
  gridWidth: number
  onChange: (px: number) => void
}

/**
 * 6 px drag handle between the frozen grid and the timeline. It lives inside the
 * scrolled content, so it is offset by `scrollLeft` to stay pinned to the right
 * edge of the sticky grid pane.
 */
const Splitter: React.FC<Props> = ({ gridWidth, onChange }) => {
  const { t } = useTranslation()
  const { scrollLeft } = useGanttLayout()
  const [dragging, setDragging] = useState(false)
  const origin = useRef({ x: 0, width: 0 })

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    origin.current = { x: e.clientX, width: gridWidth }
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
  }, [gridWidth])

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    onChange(origin.current.width + (e.clientX - origin.current.x))
  }, [dragging, onChange])

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    setDragging(false)
  }, [dragging])

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('Schedule_resize_grid')}
      data-gantt-splitter
      className={cn('absolute top-0 bottom-0 w-1.5 z-30 cursor-col-resize touch-none select-none hover:bg-primary/40', dragging && 'bg-primary/60')}
      // ui-drift: dynamic
      style={{ left: scrollLeft + gridWidth - 3 }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    />
  )
}

export default Splitter
